/**
 * Card type detection based on card number patterns
 */

import { CardType } from '../types/CardData';

interface CardPattern {
  type: CardType;
  patterns: RegExp[];
  lengths: number[];
  cvvLength: number;
}

const CARD_PATTERNS: CardPattern[] = [
  {
    type: 'visa',
    patterns: [/^4/],
    lengths: [13, 16],
    cvvLength: 3,
  },
  {
    type: 'mastercard',
    patterns: [/^5[1-5]/, /^2(22[1-9]|2[3-9][0-9]|[3-6][0-9]{2}|7[0-1][0-9]|720)/],
    lengths: [16],
    cvvLength: 3,
  },
  {
    type: 'amex',
    patterns: [/^3[47]/],
    lengths: [15],
    cvvLength: 4,
  },
  {
    type: 'discover',
    patterns: [], //handled by helper
    lengths: [16],
    cvvLength: 3,
  },
];

/**
 * Detects the card type based on the card number
 * @param cardNumber - Card number string (can include spaces/dashes)
 * @returns Card type
 */
export function detectCardType(cardNumber: string): CardType {
  if (!cardNumber) return 'unknown';

  // Remove all non-digit characters
  const digits = cardNumber.replace(/\D/g, '');

  // Discover only (6011, 644-649, 65, 622126-622925) — JCB, Diners Club, and UnionPay are NOT supported
  if (isDiscover(digits)) {
    return 'discover';
  }

  // Try to match against known patterns
  for (const card of CARD_PATTERNS) {
    if (card.type === 'discover') continue; // skip, handled above
    for (const pattern of card.patterns) {
      if (pattern.test(digits)) {
        return card.type;
      }
    }
  }

  return 'unknown';
}

/**
 * Gets the expected CVV length for a card type
 * @param cardType - Card type
 * @returns Expected CVV length (3 or 4)
 */
export function getExpectedCvvLength(cardType: CardType): number {
  const card = CARD_PATTERNS.find((c) => c.type === cardType);
  return card?.cvvLength ?? 3;
}

/**
 * Gets the valid card number lengths for a card type
 * @param cardType - Card type
 * @returns Array of valid lengths
 */
export function getValidCardLengths(cardType: CardType): number[] {
  const card = CARD_PATTERNS.find((c) => c.type === cardType);
  return card?.lengths ?? [13, 14, 15, 16, 17, 18, 19];
}

/**
 * Checks if a card number length is valid for the detected card type
 * @param cardNumber - Card number string
 * @returns true if length is valid for the card type
 */
export function isValidCardLength(cardNumber: string): boolean {
  const digits = cardNumber.replace(/\D/g, '');
  const cardType = detectCardType(digits);

  if (cardType === 'unknown') {
    // For unknown cards, accept common lengths
    return digits.length >= 13 && digits.length <= 19;
  }

  const validLengths = getValidCardLengths(cardType);
  return validLengths.includes(digits.length);
}

/**
 * Gets a human-readable card type name
 * @param cardType - Card type
 * @returns Display name
 */
export function getCardTypeName(cardType: CardType): string {
  const names: Record<CardType, string> = {
    visa: 'Visa',
    mastercard: 'Mastercard',
    amex: 'American Express',
    discover: 'Discover',
    unknown: 'Unknown',
  };
  return names[cardType];
}

/**
 * Detects Discover cards only (BINs: 6011, 644-649, 65, 622126-622925).
 * JCB, Diners Club, and UnionPay are intentionally NOT supported.
 */
function isDiscover(cardNumber: string): boolean {
  const digits = cardNumber.replace(/\D/g, '');
  return /^(6011|64[4-9]|65|6221[2-9]|622[2-8]|6229[01]|62292[0-5])/.test(digits);
}

